import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { BasicButton, ButtonType, Icon, IconSize } from "@gaming/xds-react-js";
import { Launch } from "@gaming/xds-react-js-icons";

import { DevelopmentResources } from "../../components/DevelopmentResources";
import { PageLoadingSpinner } from "../../components/PageLoadingSpinner";
import { PartnerPageLayout } from "../../components/PartnerPageLayout";
import { ScreenReaderHeading } from "../../components/ScreenReaderHeading";
import {
  ActivateRetailConsoleAsDevKitUrl,
  EdsUrl,
  gdkMiddlewareRequestPath,
  partnerAccessDeniedErrors,
  partnerAccessDeniedPath,
  partnerCenterDeviceManagementUrl,
  partnerQueryParameters,
  PlayFabUrl,
  xboxDevModeAppStoreUrl,
} from "../../constants";
import { useRequireAuth } from "../../constants/urlsClientSide";
import { getImage, ImageType } from "../../helpers/imageHelper";
import { PartnerAccessLevel, usePartnerRouteGuard } from "../../helpers/usePartnerRouteGuard";
import { useStrings } from "../../localization/localizationHelpers";
import { getXboxOnboardData } from "../../redux/selectors/xboxOnboardSelectors";
import * as styles from "./PartnerDevelopmentHardwarePage.module.css";

export const PartnerDevelopmentHardwarePage = () => {
  const isPageReady = usePartnerRouteGuard(PartnerAccessLevel.NdaSigned);
  const isSignedIn = useRequireAuth();
  const data = useSelector(getXboxOnboardData);
  const history = useHistory();
  const isHubAdmin = data?.isHubAdmin === true;
  const hasFullAccess = isHubAdmin && data?.hasDevelopmentHardwareAccess === true;
  const [
    pageLabel,
    consoleTitle,
    consoleDescription,
    installInstruction,
    devModeAppLabel,
    installInstructionSuffix,
    validateInstruction,
    partnerCenterLabel,
    deployInstruction,
    learnMore,
    consoleTerms,
    orderTitle,
    orderDescription,
    deviceServicesLabel,
    availabilityNote,
    hardwareDescription,
    middlewareTitle,
    middlewareDescription,
    middlewareAction,
    benefitsTitle,
    benefitsDescription,
    playFabTitle,
    playFabDescription,
    connectPlayersTitle,
    connectPlayersDescription,
    multiplayerTitle,
    multiplayerDescription,
    engagementTitle,
    engagementDescription,
  ] = useStrings(
    "developmentHardwareLabel",
    "developmentHardwareConsoleTitle",
    "developmentHardwareConsoleDescription",
    "developmentHardwareInstallInstruction",
    "developmentHardwareDevModeAppLabel",
    "developmentHardwareInstallInstructionSuffix",
    "developmentHardwareValidateInstruction",
    "developmentHardwarePartnerCenterLabel",
    "developmentHardwareDeployInstruction",
    "learnMore",
    "developmentHardwareConsoleTerms",
    "developmentHardwareOrderTitle",
    "developmentHardwareOrderDescription",
    "developmentHardwareDeviceServicesLabel",
    "developmentHardwareAvailabilityNote",
    "developmentHardwareSectionDescription",
    "developmentMiddlewareTitle",
    "developmentMiddlewareDescription",
    "developmentMiddlewareAction",
    "developmentPreviewBenefitsTitle",
    "developmentBenefitsDescription",
    "developmentPlayFabTitle",
    "developmentPlayFabDescription",
    "developmentPlayFabConnectTitle",
    "developmentPlayFabConnectDescription",
    "developmentPlayFabMultiplayerTitle",
    "developmentPlayFabMultiplayerDescription",
    "developmentPlayFabEngagementTitle",
    "developmentPlayFabEngagementDescription"
  );
  const [
    hardwareTitle,
    hardwarePreviewDescription,
    previewBenefitsTitle,
    benefitsPreviewDescription,
  ] = useStrings(
    "developmentPreviewHardwareTitle",
    "developmentPreviewHardwareDescription",
    "developmentPreviewBenefitsTitle",
    "developmentPreviewBenefitsDescription"
  );

  useEffect(() => {
    if (isPageReady && data && !isHubAdmin) {
      history.replace(
        `${partnerAccessDeniedPath}?${partnerQueryParameters.accessDeniedError}=${partnerAccessDeniedErrors.admin}`
      );
    }
  }, [data, history, isHubAdmin, isPageReady]);

  if (!isPageReady || !isSignedIn || !data) {
    return (
      <PartnerPageLayout breadcrumbLabel={pageLabel}>
        <PageLoadingSpinner />
      </PartnerPageLayout>
    );
  }
  if (!isHubAdmin) {
    return null;
  }
  if (!hasFullAccess) {
    return (
      <PartnerPageLayout breadcrumbLabel={pageLabel}>
        <ScreenReaderHeading>{pageLabel}</ScreenReaderHeading>
        <section className={styles.previewFeatures}>
          <article className={styles.previewFeatureCard}>
            <div>
              <h2>{hardwareTitle}</h2>
              <p>{hardwarePreviewDescription}</p>
            </div>
            <img
              className={styles.previewHardwareImage}
              src={getImage("XRAD_CTA_Hero", ImageType.Png)}
              alt=""
            />
          </article>
          <article className={styles.previewFeatureCard}>
            <div>
              <h2>{previewBenefitsTitle}</h2>
              <p>{benefitsPreviewDescription}</p>
            </div>
            <img
              className={styles.previewBenefitsImage}
              src={getImage("landing_services_screen_desktop", ImageType.Png)}
              alt=""
            />
          </article>
        </section>

        <DevelopmentResources />
      </PartnerPageLayout>
    );
  }

  return (
    <PartnerPageLayout breadcrumbLabel={pageLabel}>
      <ScreenReaderHeading>{pageLabel}</ScreenReaderHeading>
      <section className={styles.hardware}>
        <h2>{hardwareTitle}</h2>
        <p>{hardwareDescription}</p>
        <div className={styles.featureGrid}>
          <article className={styles.consoleCard}>
            <div>
              <h3>{consoleTitle}</h3>
              <p>{consoleDescription}</p>
              <ol>
                <li>
                  {installInstruction}{" "}
                  <a href={xboxDevModeAppStoreUrl} className={styles.inlineLink}>
                    {devModeAppLabel}
                  </a>{" "}
                  {installInstructionSuffix}
                </li>
                <li>
                  {validateInstruction}{" "}
                  <a
                    href={partnerCenterDeviceManagementUrl}
                    className={styles.inlineLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {partnerCenterLabel}
                  </a>
                </li>
                <li>{deployInstruction}</li>
              </ol>
              <BasicButton
                as={"a"}
                buttonType={ButtonType.Brand}
                className={styles.cardAction}
                href={ActivateRetailConsoleAsDevKitUrl}
                target="_blank"
                rel="noreferrer"
              >
                {learnMore}
                <Icon aria-hidden="true" icon={Launch} iconSize={IconSize.XSmall} />
              </BasicButton>
            </div>
            <img src={getImage("XRAD_CTA_Hero", ImageType.Png)} alt="" />
            <p className={styles.consoleTerms}>{consoleTerms}</p>
          </article>
          <article className={styles.middlewareCard}>
            <h3>{middlewareTitle}</h3>
            <p>{middlewareDescription}</p>
            <BasicButton
              as={"a"}
              buttonType={ButtonType.Secondary}
              className={`${styles.cardAction} ${styles.playFabAction}`}
              href={gdkMiddlewareRequestPath}
            >
              {middlewareAction}
            </BasicButton>
            <img
              className={styles.middlewareImage}
              src={getImage("request-middleware-development-page-tiles", ImageType.Png)}
              alt=""
            />
          </article>
        </div>
      </section>

      <section className={styles.orders}>
        <h2>{orderTitle}</h2>
        <p>
          {orderDescription}{" "}
          <a href={EdsUrl} className={styles.inlineLink} target="_blank" rel="noreferrer">
            {deviceServicesLabel}
          </a>
          <small>{availabilityNote}</small>
        </p>
      </section>

      <section className={styles.benefits}>
        <h2>{benefitsTitle}</h2>
        <p>{benefitsDescription}</p>
        <article className={styles.playFabCard}>
          <h3>{playFabTitle}</h3>
          <p>{playFabDescription}</p>
          <div className={styles.playFabFeatureGrid}>
            <a className={styles.playFabFeature} href={PlayFabUrl} target="_blank" rel="noreferrer">
              <h4>{connectPlayersTitle}</h4>
              <p>{connectPlayersDescription}</p>
            </a>
            <a className={styles.playFabFeature} href={PlayFabUrl} target="_blank" rel="noreferrer">
              <h4>{multiplayerTitle}</h4>
              <p>{multiplayerDescription}</p>
            </a>
            <a className={styles.playFabFeature} href={PlayFabUrl} target="_blank" rel="noreferrer">
              <h4>{engagementTitle}</h4>
              <p>{engagementDescription}</p>
            </a>
          </div>
        </article>
      </section>

      <DevelopmentResources />
    </PartnerPageLayout>
  );
};
